<script lang="ts">
    import vm from '@journeys/Journeys.viewmodel';
    import { JourneyStatus } from '@journeys/Enums';
    import MultiSelect from '@shared/components/MultiSelect/MultiSelect.vue';
    import BSFormInputText from '@shared/components/BSFormInputText.vue';
    import BSFormInputTextarea from '@shared/components/BSFormInputTextarea.vue';
    import ScrollableContainer from '@shared/components/ScrollableContainer.vue';

    const component = {
        name: 'Menu_JourneySettings',
    };

    export default {
        name: component.name,
    };
</script>

<script lang="ts" setup>
    import { inject, ref } from 'vue';
    import { type TagsProvide } from '@shared/components/Tags/TagsTypes';
    import JourneyCopyToAccountModal from '@journeys/components/JourneyCopyToAccountModal.vue';
    import FluidButton from '@shared/components/FluidButton.vue';
    import { bsAlert, AlertTypes } from '@shared/components/BsAlerts';
    import AclEditor from '@cwd/Acl/AclEditor.vue';
    import { type AccessControlList, type CurrentObject } from '@cwd/Acl/Acl.access';
    const tags = inject('tags') as TagsProvide;

    const { activeJourney } = vm;

    const copyModalOpen = ref(false);
    const aclModalOpen = ref(false);

    function handleCopied(accountNames: string[]) {
        copyModalOpen.value = false;
        bsAlert({
            title: $Resources.JourneyCopyToAccountSuccessText.replace('{AccountName}', accountNames.join(', ')),
            type: AlertTypes.Success,
        });
    }

    function onSaveAcl(acl: AccessControlList) {
        aclModalOpen.value = false;
        activeJourney.AccessControlList = acl as unknown as typeof activeJourney.AccessControlList;
    }
</script>

<template>
    <ScrollableContainer>
        <h2 class="h3">{{ $Resources.JourneySettingsHeading }}</h2>

        <BSFormInputText class="form-group" v-model="activeJourney.Title" :label="$Resources.TitleLabel" :placeholder="$Resources.TitleLabel"></BSFormInputText>

        <div class="form-group">
            <span class="label-look d-block">{{ $Resources.StatusLabel }}:</span>
            <strong>{{ $Resources['JourneyStatus' + JourneyStatus[activeJourney.Status]] }}</strong>
        </div>

        <div v-if="activeJourney.CopiedFromAccountName" class="form-group">
            <span class="label-look d-block">{{ $Resources.JourneyCopiedFromAccountNote.replace('{AccountName}', activeJourney.CopiedFromAccountName) }}</span>
        </div>

        <BSFormInputTextarea
            class="form-group"
            v-model="activeJourney.Description"
            :label="$Resources.DescriptionLabel"
            :placeholder="$Resources.DescriptionLabel"></BSFormInputTextarea>

        <MultiSelect
            class="form-group"
            :loading="!tags.state.loaded"
            :items="tags.state.list"
            :selectedItems="activeJourney.Tags"
            :label="$Resources.TagsLabel"
            displayProp="Title"
            selectProp="TagId"
            @update:selectedItems="(newVal) => (activeJourney.Tags = newVal)"
            @newItem="(newVal) => tags.create(newVal, activeJourney.Tags)"></MultiSelect>

        <!-- Same open/closed-lock + btn-primary + EditAclBtnOff/On convention already used for
             Issue/Survey/SMS/Event/Lists (see e.g. _SettingsTab.cshtml) -- ported here so the button
             actually indicates when this journey has an ACL set, instead of always reading the same
             regardless of state. -->
        <button
            type="button"
            class="btn btn-default btn-block btn-flex"
            :class="{ 'btn-primary': (activeJourney.AccessControlList?.length ?? 0) > 0 }"
            @click="aclModalOpen = true">
            <template v-if="(activeJourney.AccessControlList?.length ?? 0) > 0">
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round">
                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                    <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                </svg>
                <span>{{ $Resources.EditAclBtnOn }}</span>
            </template>
            <template v-else>
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round">
                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                    <path d="M7 11V7a5 5 0 0 1 9.9-1"></path>
                </svg>
                <span>{{ $Resources.EditAclBtnOff }}</span>
            </template>
        </button>

        <FluidButton
            class="btn btn-default btn-block m-md-t"
            :text="$Resources.JourneyCopyToAccountBtn"
            @click="copyModalOpen = true" />
    </ScrollableContainer>

    <AclEditor
        :selected-groups="
            activeJourney.AccessControlList?.map(
                (acl) =>
                    ({
                        ...acl,
                        GroupId: acl.PrincipalId,
                        AccessControlList: acl,
                    }) as CurrentObject
            ) || []
        "
        type="Journey"
        :objectId="activeJourney.JourneyId!"
        :open="aclModalOpen"
        @save="onSaveAcl"
        @close="aclModalOpen = false" />

    <JourneyCopyToAccountModal
        :show="copyModalOpen"
        :journeyId="activeJourney.JourneyId!"
        :currentAccountId="activeJourney.AccountId"
        @close="copyModalOpen = false"
        @copied="handleCopied" />
</template>

<style></style>
